import { BLOCK_CSP_META, blockRemoteContent } from "./SandboxedMailBody";

/**
 * T-195 — Mail drucken.
 *
 * Zwei Teile:
 *  - `baueDruckDokument` (rein): baut aus Kopfdaten und Mailtext ein
 *    eigenständiges HTML-Dokument — Mailkopf wie in Outlook, darunter der Text,
 *    Anhänge nur als Namensliste. Der Text läuft durch DENSELBEN Schutz wie die
 *    Anzeige (`blockRemoteContent` = DOMPurify + Remote-Block, im Block-Modus
 *    zusätzlich die CSP-Meta). Gedruckt wird also nie mehr, als zu sehen ist:
 *    blockierte externe Bilder bleiben auch im Ausdruck blockiert.
 *  - `druckeHtml`: legt das Dokument in eine versteckte iframe im selben
 *    Dokument (kein `window.open`, das Edge blocken könnte) und ruft dort den
 *    Druckdialog auf. Sandbox nur `allow-same-origin allow-modals` — KEIN
 *    `allow-scripts` (Mail-HTML ist fremd, vgl. F-01 in SandboxedMailBody).
 */

export type DruckAdresse = { name?: string | null; address?: string | null };

export type DruckNachricht = {
    betreff: string;
    von: DruckAdresse | null;
    an: DruckAdresse[];
    cc: DruckAdresse[];
    /** Nur wenn die Ansicht sie zeigt (gesicherte Mail-Dateien). */
    bcc?: DruckAdresse[];
    datum: Date | string | null;
    /** HTML des Mailtexts. Fehlt er, steht `vorschau` als reiner Text da. */
    bodyHtml?: string | null;
    vorschau?: string | null;
    anhaenge?: { name: string; groesse?: number | null }[];
};

const esc = (s: string): string =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

export function formatDruckAdresse(a: DruckAdresse | null | undefined): string {
    if (!a) return "";
    const name = (a.name ?? "").trim();
    const adr = (a.address ?? "").trim();
    if (name && adr && name.toLowerCase() !== adr.toLowerCase()) return `${name} <${adr}>`;
    return name || adr;
}

function formatDruckDatum(d: Date | string | null): string {
    if (!d) return "";
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return typeof d === "string" ? d : "";
    return date.toLocaleString("de-DE", {
        weekday: "long",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatGroesse(bytes: number | null | undefined): string {
    if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

const DRUCK_CSS = `
@page { margin: 15mm; }
html, body { background: #fff; color: #000; }
body { font: 11pt "Segoe UI", Arial, sans-serif; margin: 0; }
.nachricht + .nachricht { border-top: 2px solid #666; margin-top: 18px; padding-top: 12px; break-before: auto; }
h1.betreff { font-size: 15pt; font-weight: 600; margin: 0 0 8px; word-break: break-word; }
table.kopf { border-collapse: collapse; margin-bottom: 10px; border-bottom: 1px solid #999; width: 100%; }
table.kopf th { text-align: left; vertical-align: top; font-weight: 600; padding: 1px 12px 1px 0; white-space: nowrap; width: 1%; }
table.kopf td { padding: 1px 0; word-break: break-word; }
table.kopf tr:last-child th, table.kopf tr:last-child td { padding-bottom: 6px; }
.text { overflow-wrap: anywhere; }
.text img { max-width: 100%; height: auto; }
.text table { max-width: 100%; }
.vorschau { white-space: pre-wrap; color: #333; font-style: italic; }
.anhaenge { margin-top: 12px; padding-top: 6px; border-top: 1px solid #ccc; font-size: 10pt; }
.anhaenge ul { margin: 4px 0 0; padding-left: 18px; }
`;

function kopfZeile(label: string, wert: string): string {
    return wert ? `<tr><th>${esc(label)}:</th><td>${esc(wert)}</td></tr>` : "";
}

function nachrichtHtml(n: DruckNachricht, blockRemote: boolean, mitBetreff: boolean): string {
    const an = n.an.map(formatDruckAdresse).filter(Boolean).join("; ");
    const cc = n.cc.map(formatDruckAdresse).filter(Boolean).join("; ");
    const bcc = (n.bcc ?? []).map(formatDruckAdresse).filter(Boolean).join("; ");
    const kopf =
        `<table class="kopf">` +
        kopfZeile("Von", formatDruckAdresse(n.von)) +
        kopfZeile("Gesendet", formatDruckDatum(n.datum)) +
        kopfZeile("An", an) +
        kopfZeile("Cc", cc) +
        kopfZeile("Bcc", bcc) +
        kopfZeile("Betreff", n.betreff || "(Kein Betreff)") +
        `</table>`;
    const text = n.bodyHtml
        ? `<div class="text">${blockRemoteContent(n.bodyHtml, { block: blockRemote, placeholder: false }).html}</div>`
        : `<div class="vorschau">${esc(n.vorschau ?? "")}</div>`;
    const liste = (n.anhaenge ?? []).filter((a) => a.name);
    const anhaenge = liste.length
        ? `<div class="anhaenge"><strong>${liste.length === 1 ? "1 Anhang" : `${liste.length} Anhänge`}</strong><ul>` +
          liste
              .map((a) => {
                  const g = formatGroesse(a.groesse);
                  return `<li>${esc(a.name)}${g ? ` (${esc(g)})` : ""}</li>`;
              })
              .join("") +
          `</ul></div>`
        : "";
    const titel = mitBetreff ? `<h1 class="betreff">${esc(n.betreff || "(Kein Betreff)")}</h1>` : "";
    return `<section class="nachricht">${titel}${kopf}${text}${anhaenge}</section>`;
}

/**
 * Baut das Druckdokument. Bei mehreren Nachrichten (Konversation) steht der
 * Betreff einmal oben, darunter je Nachricht ein Block — in der übergebenen
 * Reihenfolge (= Reihenfolge der Anzeige).
 */
export function baueDruckDokument(nachrichten: DruckNachricht[], opt: { blockRemote: boolean }): string {
    const erste = nachrichten[0];
    const titel = erste?.betreff || "E-Mail";
    const blocks = nachrichten.map((n, i) => nachrichtHtml(n, opt.blockRemote, i === 0)).join("");
    return (
        `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8">` +
        (opt.blockRemote ? BLOCK_CSP_META : "") +
        `<title>${esc(titel)}</title><style>${DRUCK_CSS}</style></head><body>${blocks}</body></html>`
    );
}

/** Höchstens so lange wird auf Bilder gewartet, bevor trotzdem gedruckt wird. */
export const DRUCK_BILD_FRIST_MS = 10_000;
/** Sicherheitsnetz, falls `afterprint` nie kommt: dann wird trotzdem aufgeräumt. */
export const DRUCK_AUFRAEUM_FRIST_MS = 60_000;

const warte = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function warteAufBilder(doc: Document, frist: number): Promise<void> {
    const offen = Array.from(doc.images ?? []).filter((img) => !img.complete);
    if (offen.length === 0) return Promise.resolve();
    const alle = Promise.all(
        offen.map(
            (img) =>
                new Promise<void>((r) => {
                    img.addEventListener("load", () => r(), { once: true });
                    img.addEventListener("error", () => r(), { once: true });
                }),
        ),
    ).then(() => undefined);
    return Promise.race([alle, warte(frist)]);
}

let laufenderDruck: Promise<void> | null = null;

/**
 * Druckt ein fertiges HTML-Dokument über eine versteckte iframe. Ein zweiter
 * Aufruf, solange noch ein Druck läuft, öffnet keinen zweiten Dialog.
 * Lehnt ab, wenn der Browser den Druck nicht zulässt.
 */
export function druckeHtml(html: string): Promise<void> {
    if (laufenderDruck) return laufenderDruck;
    const p = new Promise<void>((resolve, reject) => {
        const iframe = document.createElement("iframe");
        iframe.setAttribute("sandbox", "allow-same-origin allow-modals");
        iframe.setAttribute("aria-hidden", "true");
        iframe.setAttribute("tabindex", "-1");
        iframe.title = "Druckansicht";
        // Nicht `display:none`/`visibility:hidden` — manche Engines drucken
        // dann leer. Außerhalb des Sichtbereichs mit Nullgröße genügt.
        iframe.style.cssText = "position:fixed;left:-10000px;top:0;width:0;height:0;border:0;";
        let erledigt = false;
        let notfall: ReturnType<typeof setTimeout> | undefined;
        const ende = (fehler?: unknown) => {
            if (erledigt) return;
            erledigt = true;
            if (notfall) clearTimeout(notfall);
            iframe.remove();
            laufenderDruck = null;
            if (fehler) reject(fehler);
            else resolve();
        };
        iframe.addEventListener(
            "load",
            () => {
                void (async () => {
                    try {
                        const win = iframe.contentWindow;
                        const doc = iframe.contentDocument;
                        if (!win || !doc) throw new Error("Druckansicht nicht erreichbar");
                        await warteAufBilder(doc, DRUCK_BILD_FRIST_MS);
                        try {
                            await Promise.race([doc.fonts?.ready ?? Promise.resolve(), warte(3000)]);
                        } catch {
                            /* Schriften sind kein Grund, nicht zu drucken */
                        }
                        win.addEventListener("afterprint", () => setTimeout(() => ende(), 0), { once: true });
                        notfall = setTimeout(() => ende(), DRUCK_AUFRAEUM_FRIST_MS);
                        win.focus();
                        win.print();
                    } catch (e) {
                        ende(e ?? new Error("Drucken fehlgeschlagen"));
                    }
                })();
            },
            { once: true },
        );
        iframe.srcdoc = html;
        document.body.appendChild(iframe);
    });
    laufenderDruck = p;
    return p;
}
